import React, { Component } from "react";

import FileSaver from "file-saver";
import {
  getUTCDate,
  selectFormat,
  capitalizeKey,
  getAllKeys,
} from "./../Utils/Utils.js";
import moment from "moment";
import Papa from "papaparse";
import Downshift from "downshift";
import clsx from "clsx";

import more_icon_on from "./../../i/icons/more.svg";
import more_icon_off from "./../../i/icons/more-black.svg";
import download_icon_on from "./../../i/icons/download.svg";
import download_icon_off from "./../../i/icons/download-black.svg";

class CSVExport extends Component {
  constructor(props) {
    super(props);
    this.state = { hovered: false };
  }

  defineDateFormat(dateString) {
    // 09/15  - default format
    let fmt = "MM/DD";
    let viewFmt = "YYYY-MM-DD";

    // in the case that dateString has only MM/DD.. return the date without the default format wich
    // defines the year/yyyy
    if (dateString.length <= 5) viewFmt = "MM-DD";

    // 'Jan', 'Feb', ...
    if (dateString.split("/").length < 2 && dateString.length === 3) {
      fmt = "MMM";
      viewFmt = "MMM YYYY";
    } else if (dateString.length > 8 && dateString.length < 15) {
      fmt = "MM/DD hh:mm";
      viewFmt = "MM-DD hh:mm";
    } else if (dateString.length > 15) {
      fmt = "YYYY-MM-DDThh:mm:ssZ";
      viewFmt = "YYYY-MM-DD hh:mmZ";
    }
    return {
      format: fmt,
      viewFormat: viewFmt,
    };
  }

  getDate(d, opts) {
    // Retrieve the current date using the provided format.
    const curDate = getUTCDate(d.full_date || d.date, opts.date.format);

    // Return the formatted date in the UTC time zone.
    return moment(curDate).utc().format(opts.date.viewFormat);
  }

  itemToString = (i) => (i ? i.name : "");

  // Table data
  prepareTableDataExport(data = null, props) {
    const opts = props.csv_export || {};
    // get all fields from the csv_export
    const fields = opts.fields;

    // if the data is empty.. return
    if (data == null || !data.length) {
      return null;
    }

    // prepare data
    const preparedData = data.map((d) => {
      // creates an object containing the data
      const _d = {};

      // uses the 'fields' as reference to set the attributes into the _d object
      fields.forEach((f) => {
        // if the field contains 'path'
        if (f.path) {
          // add a new attribute setting the attribute [title] from the fields as name
          _d[f.title] = d[f.name][f.path];
        } else {
          // if the type of the attribute of the element from the data (according the field name) is an object
          // set a new attribute to the object return setting the attribute name according the field title
          // and its value is equal to the element data + field name value
          if (typeof d[f.name] === "object") _d[f.title] = d[f.name].value;
          // if not, set an attribute which has the field.title as name and its value is from the
          // data element having as attribute value the field.name
          else _d[f.title] = d[f.name];
        }
        // if the field has a specific format.. return it formatted properly
        if (f.format) _d[f.title] = selectFormat(f.format, _d[f.title]);
      });
      return _d;
    });
    return preparedData;
  }

  // Bar, Line charts
  prepareChartDataExport(_data = null, props) {
    const opts = props.csv_export || {};

    if (_data == null || !_data.length) {
      return null;
    }
    const data = _data.filter((d) => d);

    opts.date = Object.assign(
      {
        name: "date",
        title: "Date",
        format: "MM/DD",
        viewFormat: "YYYY-MM-DD",
      },
      opts.date
    );
    opts.series = Object.assign(
      { title: "Series Name", format: "csv_number" },
      opts.series
    );
    opts.metric = Object.assign({ title: "Metric" }, opts.metric);
    opts.value = Object.assign({ title: "Value" }, opts.value);
    opts.exclude = opts.exclude || ["date", "articles", "full_date"];

    // prepare data
    const preparedData = [];

    if (data.length) {
      if (!opts.date.no_date && !opts.date.defined) {
        // update date format based on data. It can be MM/DD, MMM
        const dateFmt = this.defineDateFormat(data[0][opts.date.name]);
        opts.date.format = dateFmt.format;
        opts.date.viewFormat = dateFmt.viewFormat;
      }

      let values = opts.values || getAllKeys(data);
      if (opts.exclude.length)
        values = values.filter((key) => !opts.exclude.includes(key));

      data.forEach((d) => {
        values.forEach((series) => {
          if (typeof d[series] === "undefined" || d[series] === null) return;

          const _d = {};
          _d[opts.date.title] = opts.date.no_date
            ? d[opts.date.name]
            : this.getDate(d, opts);
          _d[opts.series.title] =
            typeof d[series + "_label"] === "undefined"
              ? capitalizeKey(series)
              : d[series + "_label"];
          _d[opts.metric.title] = opts.metric.value || props.title;

          let value;
          if (opts.series.path) {
            value = d[series][opts.series.path];
          } else {
            if (typeof d[series] === "object") value = d[series].value;
            else value = d[series];
          }
          _d[opts.value.title] = selectFormat(opts.series.format, value, "0,0");

          preparedData.push(_d);
        });
      });
    }
    return preparedData;
  }

  // Nested Bar charts
  prepareChartNestedDataExport(data = null, props) {
    const opts = props.csv_export || {};

    if (data == null || !data.length) {
      return null;
    }

    opts.date = Object.assign(
      {
        name: "date",
        title: "Date",
        format: "MM/DD",
        viewFormat: "YYYY-MM-DD",
      },
      opts.date
    );
    opts.series = Object.assign({ title: "Series Name" }, opts.series);
    opts.breakouts = Object.assign({ title: "Breakout Name" }, opts.breakouts);
    opts.metric = Object.assign({ title: "Metric" }, opts.metric);
    opts.value = Object.assign(
      { title: "Value", format: "csv_number" },
      opts.value
    );
    opts.exclude = opts.exclude || ["date"];
    opts.exclude_breakouts = opts.exclude_breakouts || ["total"]; // array of Breakout Names like ["total", "web", "android", ...]
    opts.no_breakouts = opts.no_breakouts || []; // array of Series Names like ["amazon", "apple+", "ca", ...]

    // prepare data
    const preparedData = [];
    if (data.length) {
      // update date format based on data. It can be MM/DD, MMM
      const dateFmt = this.defineDateFormat(data[0][opts.date.name]);
      // format the date according the `dateFmt` or the defined one from options
      opts.date.format = opts.date.defined ? opts.date.format : dateFmt.format;
      opts.date.viewFormat = opts.date.defined
        ? opts.date.viewFormat
        : dateFmt.viewFormat;

      let values = opts.values || getAllKeys(data);
      if (opts.exclude.length)
        values = values.filter((key) => !opts.exclude.includes(key));

      data.forEach((d) => {
        values.forEach((series) => {
          if (typeof d[series] === "undefined") return;

          if (opts.no_breakouts.includes(series)) {
            const _d = {};
            _d[opts.date.title] = this.getDate(d, opts);
            _d[opts.series.title] =
              typeof d[series + "_label"] === "undefined"
                ? capitalizeKey(series)
                : d[series + "_label"];
            _d[opts.breakouts.title] = "Total";
            _d[opts.metric.title] = opts.metric.value || props.title;

            let value;
            if (opts.series.path) {
              value = d[series][opts.series.path];
            } else {
              if (typeof d[series] === "object") value = d[series].total;
              else value = d[series];
            }
            _d[opts.value.title] = selectFormat(
              opts.value.format,
              value,
              "0,0"
            );
            preparedData.push(_d);
          } else {
            let breakouts =
              opts.breakouts.values ||
              (opts.series.path
                ? Object.keys(d[series][opts.series.path])
                : Object.keys(d[series]));
            if (opts.exclude_breakouts.length) {
              breakouts = breakouts.filter(
                (key) => !opts.exclude_breakouts.includes(key)
              );
            }
            breakouts.forEach((breakout) => {
              const _d = {};
              _d[opts.date.title] = this.getDate(d, opts);
              _d[opts.series.title] =
                typeof d[series + "_label"] === "undefined"
                  ? capitalizeKey(series)
                  : d[series + "_label"];
              _d[opts.breakouts.title] = capitalizeKey(breakout);
              _d[opts.metric.title] = opts.metric.value || props.title;

              let value;
              if (opts.series.path) {
                value = d[series][opts.series.path][breakout];
              } else {
                if (typeof d[series][breakout] === "object")
                  value = d[series][breakout].value;
                else value = d[series][breakout];
              }
              _d[opts.value.title] = selectFormat(
                opts.value.format,
                value,
                "0,0"
              );
              preparedData.push(_d);
            });
          }
        });
      });
    }

    return preparedData;
  }

  downloadCSV(_props) {
    let filename, preparedData, csv;
    const childRef = _props.childRef();

    if (!childRef) return;

    // use built-in-export feature for data-table
    if (childRef.builtInExportCSV) {
      const tableRef = childRef.builtInExportCSV();
      return (
        tableRef &&
        tableRef.props &&
        tableRef.props.onExport &&
        tableRef.props.onExport()
      );
    }
    if (
      childRef.current &&
      childRef.current.props &&
      childRef.current.props.onExport
    ) {
      return childRef.current.props.onExport();
    }

    const data = childRef.data
      ? childRef
      : childRef.getExportData
      ? childRef.getExportData()
      : null;
    if (data === null) return;

    const props = Object.assign({}, this.props, {
      csv_export: data.opts,
      title: this.props.TITLE,
    });

    switch (props.csv_export.type) {
      case "table":
        preparedData = this.prepareTableDataExport(data.data, props);
        break;
      case "chart":
        preparedData = this.prepareChartDataExport(
          data.data || data.graph,
          props
        );
        break;
      case "chart_nested":
        preparedData = this.prepareChartNestedDataExport(
          data.data || data.graph,
          props
        );
        break;
      default:
        preparedData = data.data;
    }

    csv = Papa.unparse(preparedData);

    if (csv == null) return;

    filename =
      ((props.csv_export && props.csv_export.title) || props.title || "export")
        .split(" ")
        .join("_") || "export";

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    FileSaver.saveAs(blob, filename + ".csv");
  }

  render() {
    const items = [
      {
        name: "Export CSV",
        icon_on: download_icon_on,
        icon_off: download_icon_off,
      },
    ];

    return this.props.children ? (
      this.props.children(this, this.props)
    ) : (
      <Downshift onSelect={() => this.downloadCSV(this.props)}>
        {({
          isOpen,
          getToggleButtonProps,
          getItemProps,
          highlightedIndex,
          toggleMenu,
        }) => {
          return (
            <div>
              <button
                {...getToggleButtonProps()}
                className="csv-export"
                onMouseEnter={() => this.setState({ hovered: true })}
                onMouseLeave={() => this.setState({ hovered: false })}
              >
                <img
                  src={this.state.hovered ? more_icon_on : more_icon_off}
                  alt="More"
                  className="csv-export-icon"
                />
              </button>

              <div style={{ position: "relative" }}>
                {isOpen && (
                  <div className="csv-export-menu">
                    <div
                      className="csv-export-header"
                      onClick={() => {
                        toggleMenu();
                      }}
                    >
                      <img src={more_icon_on} alt="More" />
                    </div>

                    {items.map((item, index) => {
                      let itemClass = clsx("csv-export-item", {
                        "csv-export-item--active": highlightedIndex === index,
                      });

                      return (
                        <div
                          key={index}
                          {...getItemProps({
                            item: this.itemToString(item),
                            className: itemClass,
                          })}
                        >
                          <img
                            className="csv-export-item-icon"
                            alt="more"
                            src={
                              highlightedIndex === index
                                ? item.icon_on
                                : item.icon_off
                            }
                          />
                          {item.name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        }}
      </Downshift>
    );
  }
}

export default CSVExport;
